import { useMemo } from 'react';
import type { FieldPath } from 'react-hook-form';

// MUI
import SaveRounded from '@mui/icons-material/SaveRounded';
import UndoRounded from '@mui/icons-material/UndoRounded';

// common
import SchemaForm from 'common/SchemaForm';
import { EMAIL_REGEX, PHONE_INPUT_NUMBER_REGEX, POSITIVE_NUMBER_REGEX } from 'config/regex';
import { genders, jobTitles, organizationTypes } from 'enum/combobox';
import { datePickerCustomStyle } from 'common/SchemaForm/element/datepicker-element/utils';

// screen
import type { UserDetail } from 'modules/account/services/useGetAccountInfo';
import { bindTextField, bindValueField } from 'modules/account/detail/helpers/formFieldHelpers';
import { convertOrgType } from 'modules/account/utils';
import { useTabsData } from 'modules/account/detail/hooks/useTabsData';
import { useAppSelector } from '@redux/store';

const UserInfoTab = () => {
  const { userInfo, unitAddress, viewOnly, setUserData, handleUpdate, toPreviousPage } =
    useTabsData();
  const { userInfo: userFromStore } = useAppSelector((state) => state.auth);
  const orgType = convertOrgType(userInfo.userAuthen?.userActualManagingOrgType || '');
  const isSamePerson = useMemo(
    () => userFromStore?.id === userInfo.id,
    [userFromStore?.id, userInfo.id]
  );

  return (
    <SchemaForm<UserDetail>
      formHookProps={{
        values: {
          ...userInfo,
          userAuthen: userInfo.userAuthen
            ? {
                ...userInfo.userAuthen,
                userActualManagingOrgType: orgType,
                username: userInfo.userAuthen.username ?? null,
                email: userInfo.userAuthen.email ?? null,
              }
            : userInfo.userAuthen,
        } as UserDetail,
      }}
      onSubmit={handleUpdate}
      globalProps={{ readOnly: Boolean(viewOnly) }}
      schema={{
        fields: ({
          formProps: {
            intl: { formatMessage },
            readOnly,
          },
          methods: { setValue },
        }) => {
          const bindText = (field: FieldPath<UserDetail>) =>
            bindTextField<UserDetail>(field, setValue, setUserData);
          const bindVal = (field: string) => bindValueField(field, setUserData);
          return {
            name: {
              mode: 'text-field',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.fullname' }),
              placeholder: formatMessage({ id: 'account.enter.fullname' }),
              rules: {
                required: true,
                minLength: {
                  value: 3,
                  message: formatMessage({ id: 'account.error.invalidFullname' }),
                },
              },
              ...bindText('name'),
            },
            'userAuthen.userActualManagingOrgType': {
              mode: 'select',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.orgType' }),
              placeholder: formatMessage({ id: 'account.select.orgType' }),
              rules: { required: true },
              options: organizationTypes,
              rawOptions: true,
              disableClearBtn: true,
              disabled: true,
            },
            'miscData.Gender': {
              mode: 'select',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.gender' }),
              placeholder: formatMessage({ id: 'account.select.gender' }),
              options: genders,
              rawOptions: true,
              disableClearBtn: true,
              ...bindVal('miscData.Gender'),
            },
            unitId: {
              mode: 'select',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.unitName' }),
              placeholder: formatMessage({ id: 'account.select.unit' }),
              rules: { required: true },
              options: [
                {
                  id: userInfo.userAuthen?.userActualManagingOrgId || '',
                  name: userInfo.userAuthen?.userActualManagingOrgName || unitAddress?.name || '',
                },
              ],
              rawOptions: true,
              disableClearBtn: true,
              disabled: true,
            },
            'miscData.Dob': {
              mode: 'datePicker',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.dob' }),
              placeholder: formatMessage({ id: 'enter' }),
              ...bindVal('miscData.Dob'),
              disableFuture: true,
              ...datePickerCustomStyle,
            },
            title: {
              mode: 'select',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.jobTitle' }),
              placeholder: formatMessage({ id: 'account.select.jobTitle' }),
              rules: { required: true },
              options: jobTitles.filter(({ organizationTypeId }) => organizationTypeId === orgType),
              rawOptions: true,
              disableClearBtn: true,
              ...bindVal('title'),
            },
            'miscData.IdDoc': {
              mode: 'text-field',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.identityNumber' }),
              placeholder: formatMessage({ id: 'account.enter.CCCD' }),
              rules: {
                pattern: {
                  value: POSITIVE_NUMBER_REGEX,
                  message: formatMessage({ id: 'account.invalid.cccd' }),
                },
              },
              showRequiredAsterisk: false,
              ...bindText('miscData.IdDoc'),
            },
            'miscData.JobPosition': {
              mode: 'text-field',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.jobPosition' }),
              placeholder: formatMessage({ id: 'account.jobPosition' }),
              ...bindText('miscData.JobPosition'),
            },
            'miscData.IdDocIssuedBy': {
              mode: 'text-field',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.issuePlace' }),
              placeholder: formatMessage({ id: 'account.enter.cccdIssuedPlace' }),
              ...bindText('miscData.IdDocIssuedBy'),
            },
            'miscData.Address': {
              mode: 'text-field',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.address' }),
              placeholder: formatMessage({ id: 'account.enter.address' }),
              ...bindText('miscData.Address'),
            },
            'miscData.IdDocIssuedAt': {
              mode: 'datePicker',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.issueDate' }),
              placeholder: formatMessage({ id: 'enter' }),
              ...bindVal('miscData.IdDocIssuedAt'),
              disableFuture: true,
              ...datePickerCustomStyle,
            },
            'miscData.Phone': {
              mode: 'text-field',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.phoneNumber' }),
              placeholder: formatMessage({ id: 'account.enter.phoneNumber' }),
              rules: {
                required: true,
                pattern: {
                  value: PHONE_INPUT_NUMBER_REGEX,
                  message: formatMessage({ id: 'account.invalid.phoneNumber' }),
                },
              },
              ...bindText('miscData.Phone'),
            },
            email: {
              mode: 'text-field',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'email' }),
              placeholder: formatMessage({ id: 'account.enter.email' }),
              rules: {
                required: true,
                pattern: { value: EMAIL_REGEX, message: formatMessage({ id: 'invalid_email' }) },
              },
              ...bindText('email'),
            },
            'userAuthen.expiredAt': {
              mode: 'datePicker',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.expiredAt' }),
              placeholder: formatMessage({ id: 'enter' }),
              rules: { required: !userInfo.noExpired },
              ...bindVal('userAuthen.expiredAt'),
              disablePast: true,
              disabled: userInfo.noExpired,
              ...datePickerCustomStyle,
            },
            'userAuthen.username': {
              mode: 'text-field',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.accountName' }),
              placeholder: formatMessage({ id: 'account.enter.accountName' }),
              rules: { required: true },
              disabled: true,
            },
            noExpired: {
              mode: 'switch',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.expiration.noExpiration' }),
              labelPlacement: 'top',
              ...bindVal('noExpired'),
            },
            status: {
              mode: 'switch',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'statusAdmin' }),
              labelPlacement: 'top',
              disabled: isSamePerson,
              onChange: (status) => {
                setUserData('status', status);
                setUserData('userAuthen.inactive', !status);
              },
            },
            blank1: { mode: 'node' },
            back: {
              mode: 'button',
              variant: 'outlined',
              startIcon: <UndoRounded />,
              children: formatMessage({ id: 'back' }),
              onClick: toPreviousPage,
              propsWrapper: { mobile: true, display: 'flex', justifyContent: 'flex-end' },
            },
            submit: {
              mode: 'button',
              type: 'submit',
              variant: 'contained',
              startIcon: <SaveRounded />,
              hidden: readOnly,
              children: formatMessage({ id: 'save' }),
              propsWrapper: { mobile: undefined },
            },
          };
        },
      }}
    />
  );
};

export default UserInfoTab;
